import { MapPin, Clock, WhatsappLogo } from "@phosphor-icons/react";
import { useLocale } from "@/i18n";

export default function BarberHero({ barber }) {
  const { t, locale } = useLocale();
  const waPrefix = barber.country === "py" ? "595" : "55";
  const WEEKDAY_LABELS = locale === "es"
    ? ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"]
    : ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

  return (
    <header className="relative" data-testid="barber-hero">
      <div className="relative h-44 overflow-hidden">
        <img
          src={`/banner-${barber.banner || 1}.webp`}
          alt=""
          fetchPriority="high"
          className="w-full h-full object-cover"
          data-testid="barber-banner"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-zinc-950" />
        <div className="absolute top-4 left-4 text-amber-500 drop-shadow-lg">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold" data-testid="shop-name">
            {barber.shop_name}
          </span>
        </div>
      </div>

      <div className={`px-4 relative z-10 ${barber.photo_url ? "-mt-12" : "pt-6"}`}>
        {barber.photo_url && (
          <div className="flex items-end gap-4">
            <img
              src={barber.photo_url}
              alt={`Foto de ${barber.name}`}
              className="w-24 h-24 rounded-full object-cover border-2 border-amber-500 shadow-xl shadow-black/50 bg-zinc-800"
              data-testid="barber-photo"
            />
          </div>
        )}

        <h1 className="text-4xl font-black tracking-tighter leading-none mt-3" data-testid="barber-name">
          {barber.name}
        </h1>
        {barber.city && (
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-500 mt-1" data-testid="barber-city">
            {barber.city}
          </p>
        )}
        {barber.description && <p className="text-sm text-zinc-400 leading-relaxed mt-3">{barber.description}</p>}

        <div className="mt-4 space-y-2 text-sm text-zinc-400">
          {barber.address && (
            <div className="flex items-center gap-2" data-testid="barber-address">
              <MapPin size={16} className="text-amber-500 shrink-0" weight="duotone" />
              {barber.address}
            </div>
          )}
          {barber.whatsapp && (
            <a
              href={`https://wa.me/${barber.whatsapp.length <= 11 ? waPrefix + barber.whatsapp : barber.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-amber-500 transition-colors"
              data-testid="barber-whatsapp"
            >
              <WhatsappLogo size={16} className="text-amber-500 shrink-0" weight="duotone" />
              {barber.whatsapp}
            </a>
          )}
          <div data-testid="barber-hours">
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-amber-500 shrink-0" weight="duotone" />
              {t("hours_title")}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 pl-6">
              {WEEKDAY_LABELS.map((label, wd) => {
                const cfg = barber.schedule?.[String(wd)];
                return (
                  <p key={wd} className="text-xs">
                    <span className="text-zinc-300 font-semibold">{label}</span>{" "}
                    {cfg ? `${cfg.open} – ${cfg.close}` : <span className="text-zinc-600">{t("closed")}</span>}
                  </p>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
